// FCM subscription helper.
//
// On first login (after Firebase config has been fetched from /notifications/fcm-config)
// we prompt for notification permission, obtain a FCM device token, and post
// it to the backend so /api/notifications/fcm-subscribe stores it.
//
// This module is a no-op when the backend reports FCM is not configured,
// so nothing bugs the user until VAPID_KEY is provided.

import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { getFirebase } from "@/lib/firebase";
import { api } from "@/lib/api";
import { toast } from "sonner";

let _initialised = false;

export async function initFcmForCurrentUser() {
  if (_initialised) return;
  _initialised = true;
  try {
    if (!(await isSupported())) return;
    // Ask the backend whether it can even receive tokens
    const { data: cfg } = await api.get("/notifications/fcm-config");
    if (!cfg.available || !cfg.vapid_key) return;

    // Wait for our OWN service worker to be active first, then register the
    // firebase-messaging SW alongside it. They coexist because they own
    // different scopes and Firebase's SW handles onBackgroundMessage only.
    if (!("serviceWorker" in navigator)) return;
    let fcmReg;
    try {
      fcmReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    } catch (e) {
      console.warn("[fcm] SW registration failed", e);
      return;
    }

    // Request permission (silently fails on denial — user chose)
    let perm = Notification.permission;
    if (perm === "default") {
      perm = await Notification.requestPermission();
    }
    if (perm !== "granted") return;

    const messaging = getMessaging(getFirebase());
    const token = await getToken(messaging, {
      vapidKey: cfg.vapid_key,
      serviceWorkerRegistration: fcmReg,
    });
    if (!token) return;

    // Persist the token server-side so future pushes reach this device
    await api.post("/notifications/fcm-subscribe", {
      token,
      device_label: navigator.userAgent.slice(0, 80),
    }).catch(() => { /* non-fatal */ });

    // Foreground handler — Firebase suppresses OS notifications when the
    // page is focused, so we render our own toast instead.
    onMessage(messaging, (payload) => {
      toast(payload.notification?.title || "PartsGrid", {
        description: payload.notification?.body,
      });
    });
  } catch (e) {
    console.warn("[fcm] init failed", e);
  }
}
